/**
 * Chat command parser and registry
 * 
 * Commands follow the syntax: /commandName arg1 arg2 ...
 */

import type { SpaceTimeClient } from '../spacetime/SpaceTimeClient';
import type { GameState } from '../model/gamestate/GameState';
import { getCommandHandler } from './commands';

/** Chat channel format expected by SpaceTimeClient */
export type ChatChannel =
    | { tag: 'System' }
    | { tag: 'Map'; value: string }
    | { tag: 'Friend' }
    | { tag: 'Guild'; value: string }
    | { tag: 'Whisper'; value: string };

/** Context provided to command handlers */
export interface CommandContext {
    gameState: GameState;
    spaceTimeClient: SpaceTimeClient;
    getLastWhisperTarget: () => string | null;
    setLastWhisperTarget: (target: string) => void;
    getLastWhisperSender: () => string | null;
    setLastWhisperSender: (sender: string) => void;
    showLocalMessage: (text: string) => void;
}

/** Result of a command execution */
export type CommandResult =
    | { handled: true; channel?: ChatChannel; text?: string; error?: string }
    | { handled: false };

/** Command handler function signature */
export type CommandHandler = (
    args: string[],
    ctx: CommandContext
) => CommandResult;

/**
 * Parse input and execute matching command if found
 * Returns null if input is not a command (doesn't start with /)
 */
export function parseAndExecute(
    input: string,
    ctx: CommandContext
): CommandResult | null {
    const trimmed = input.trim();
    if (!trimmed.startsWith('/')) {
        return null;
    }

    const parts = trimmed.slice(1).split(/\s+/);
    const commandName = parts[0]?.toLowerCase();
    const args = parts.slice(1);

    if (!commandName) {
        return null;
    }

    const handler = getCommandHandler(commandName);
    if (!handler) {
        return { handled: false };
    }

    return handler(args, ctx);
}
